import React from 'react';
import { useTranslation } from 'react-i18next';
import { Star, Users, Clock, MapPin, Building2, Route } from 'lucide-react';
import { VerifiedBadge } from './VerifiedBadge';
import './RideCard.css';

const safe = (val, decimals = 2) => {
  const num = Number(val);
  return isNaN(num) ? (decimals === 0 ? '0' : '0.00') : num.toFixed(decimals);
};

const formatDate = (dateStr, langCode) => {
  if (!dateStr) return '-';
  const localeMap = {
    de: 'de-DE', en: 'en-US', tr: 'tr-TR', ar: 'ar-SA',
    pl: 'pl-PL', pt: 'pt-BR', fr: 'fr-FR', it: 'it-IT',
    uk: 'uk-UA', es: 'es-ES'
  };
  try {
    const date = new Date(dateStr + 'T00:00:00');
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString(localeMap[langCode] || 'de-DE', { day: '2-digit', month: 'short' });
  } catch {
    return dateStr;
  }
};

export const RideCard = ({ ride, onClick, onViewRoute }) => {
  const { t, i18n } = useTranslation();
  const isCity = (ride.rideMode || ride.ride_mode) === 'city';
  const driverName = ride.driver_name || ride.driver?.name || `${ride.driver?.firstName || ''} ${ride.driver?.lastName || ''}`.trim() || t('common.driver');
  const rating = Number(ride.driver?.rating || ride.driver?.average_rating || ride.driver?.rating_avg || 0);
  const reviewCount = Number(ride.driver?.reviews || ride.driver?.total_ratings || ride.driver?.rating_count || 0);

  // Phase A3 — Corridor pickup info (only shown when passenger doesn't board at exact start)
  const pickupKm = Number(ride.pickup_distance_km || 0);
  const dropoffKm = Number(ride.dropoff_distance_km || 0);
  const detourKm = Number(ride.pickup_detour_km || 0);
  // Heuristic: if the search returned this ride via corridor (>500m off either endpoint),
  // it's an "en-route" pickup → highlight it as a key conversion driver.
  const isCorridorPickup = pickupKm > 0.5 || dropoffKm > 0.5;
  const pickupCity = ride.pickup_city || ride.start_city || '';

  return (
    <div className="ride-card" onClick={() => onClick(ride)} data-testid={`ride-card-${ride.id || ''}`}>
      {/* Top row: route + mode badge */}
      <div className="rc-top">
        <h3 className="rc-route">{ride.start_city || '?'} <span className="rc-arrow">&rarr;</span> {ride.destination_city || ride.end_city || '?'}</h3>
        <span className={`rc-mode ${isCity ? 'rc-mode-city' : 'rc-mode-long'}`} data-testid="ride-mode-badge">
          {isCity ? <Building2 size={12} /> : <Route size={12} />}
          {isCity ? t('rideMode.city') : t('rideMode.longdistance')}
        </span>
      </div>

      {/* Phase A3 — Corridor pickup banner: PROMINENT conversion-driver UI */}
      {isCorridorPickup && (
        <div
          className="rc-corridor-banner"
          data-testid={`rc-corridor-banner-${ride.id || ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (onViewRoute) onViewRoute(ride);
          }}
        >
          <div className="rc-corridor-icon">
            <MapPin size={16} strokeWidth={2.6} />
          </div>
          <div className="rc-corridor-text">
            <span className="rc-corridor-title">
              {t('corridor.pickupAlong', 'Zustieg unterwegs möglich')}
              {pickupCity ? <> · <strong>{pickupCity}</strong></> : null}
            </span>
            {(pickupKm > 0 || detourKm > 0) && (
              <span className="rc-corridor-meta">
                {pickupKm > 0 && (
                  <>{t('corridor.kmOffRoute', '{{km}} km vom Start', { km: safe(pickupKm, 1) })}</>
                )}
                {pickupKm > 0 && detourKm > 0 && <span className="rc-dot">•</span>}
                {detourKm > 0 && (
                  <>{t('corridor.detourLabel', '{{km}} km Umweg', { km: safe(detourKm, 1) })}</>
                )}
              </span>
            )}
          </div>
          {onViewRoute && (
            <button
              type="button"
              className="rc-corridor-cta"
              data-testid={`rc-view-route-${ride.id || ''}`}
              onClick={(e) => { e.stopPropagation(); onViewRoute(ride); }}
            >
              {t('corridor.viewRoute', 'Route ansehen')}
            </button>
          )}
        </div>
      )}

      {/* Middle row: time, price, seats — always visible side by side */}
      <div className="rc-stats">
        <div className="rc-stat" data-testid="ride-time">
          <Clock size={15} className="rc-stat-icon" />
          <span className="rc-stat-main">{ride.departure_time || '-'}</span>
          <span className="rc-stat-sub">{formatDate(ride.departure_date, i18n.language)}</span>
        </div>
        <div className="rc-stat rc-stat-price" data-testid="ride-price">
          <span className="rc-price">{safe(ride.price_per_seat)}€</span>
          <span className="rc-stat-sub">{t('common.perPerson')}</span>
        </div>
        <div className="rc-stat" data-testid="ride-seats">
          <Users size={15} className="rc-stat-icon" />
          <span className="rc-stat-main">{ride.seats_available ?? 0}/{ride.seats_total ?? 0}</span>
          <span className="rc-stat-sub">{t('rideCard.seatsFree')}</span>
        </div>
      </div>

      {/* Bottom row: driver info */}
      <div className="rc-driver">
        <div className="rc-avatar">
          {driverName.charAt(0)}
        </div>
        <div className="rc-driver-info">
          <span className="rc-driver-name">
            {driverName}
            {ride.driver_verified && <VerifiedBadge size="xs" showText={false} />}
          </span>
          <span className="rc-driver-rating">
            <Star size={12} fill="#fbbf24" stroke="#fbbf24" />
            {safe(rating, 1)} ({reviewCount})
          </span>
        </div>
        {ride.vehicle_info?.photo_url && (
          <img
            data-testid={`rc-vehicle-photo-${ride.id || ''}`}
            src={ride.vehicle_info.photo_url.startsWith('http') ? ride.vehicle_info.photo_url : `${process.env.REACT_APP_BACKEND_URL || ''}${ride.vehicle_info.photo_url}`}
            alt={t('rideCard.vehiclePhotoAlt', 'Auto')}
            style={{
              width: 44, height: 32, objectFit: 'cover',
              borderRadius: 6, border: '1px solid #e2e8f0',
              flexShrink: 0
            }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }}
          />
        )}
        {ride.vehicle_info?.make && (
          <span className="rc-vehicle">
            {ride.vehicle_info.make} {ride.vehicle_info.model || ''}
          </span>
        )}
        {(ride.cost_per_km || ride.pricePerKm) ? (
          <span className="rc-km-rate">{safe(ride.cost_per_km || ride.pricePerKm)}€/km</span>
        ) : null}
      </div>
    </div>
  );
};
